// "Adjust" in the "more gifts" step: the full editor for one gift, in a frame over the page.
//
// The frame is this same design page, opened for the gift's product with adjust=1 (see main.js). It is a separate page,
// so it has its own editor and the editor of the main design is not touched. They talk with messages:
//   frame  -> page   { type: "rg-adjust-ready" }                       the editor in the frame is waiting
//   page   -> frame  { type: "rg-adjust-init", sides, side, sources, originals }   the design to start from
//   frame  -> page   { type: "rg-adjust-done", sides, side, sources, originals }   the customer tapped "Use this"
// sides: { <side id>: design JSON text }. sources: [[photo id, data URL]]. originals: [[photo id, file]].
// Only messages from this same website and from the right window are read. Nothing is saved in the browser by the frame.

import { esc } from "./util.js";

const READY = "rg-adjust-ready", INIT = "rg-adjust-init", DONE = "rg-adjust-done";
const PHOTO_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const SIDE_ID_RE = /^[a-z0-9-]{1,20}$/;

// What a message may carry. Anything else is dropped.
export function readDesignMessage(data) {
  if (!data || typeof data !== "object" || !data.sides || typeof data.sides !== "object" || Array.isArray(data.sides)) return null;
  const sides = {};
  for (const [id, json] of Object.entries(data.sides)) {
    if (!SIDE_ID_RE.test(id) || typeof json !== "string" || json.length > 5 * 1024 * 1024) return null;
    sides[id] = json;
  }
  if (!Object.keys(sides).length) return null;
  const sources = new Map(), originals = new Map();
  for (const p of Array.isArray(data.sources) ? data.sources : []) if (Array.isArray(p) && PHOTO_ID_RE.test(String(p[0])) && typeof p[1] === "string" && p[1].startsWith("data:image/")) sources.set(p[0], p[1]);
  for (const p of Array.isArray(data.originals) ? data.originals : []) if (Array.isArray(p) && PHOTO_ID_RE.test(String(p[0])) && p[1] instanceof Blob) originals.set(p[0], p[1]);
  return { sides, side: typeof data.side === "string" && SIDE_ID_RE.test(data.side) ? data.side : Object.keys(sides)[0], sources, originals };
}
const packDesign = (d) => ({ sides: d.sides, side: d.side, sources: [...d.sources], originals: [...d.originals] });

// ---------- in the frame ----------

export const inFrame = () => window.parent !== window;

// Asks the page for the design to start from. Rejects when there is no page (the link was opened on its own) or it does not answer.
export function waitForDesign(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (!inFrame()) { reject(new Error("not in a frame")); return; }
    const timer = setTimeout(() => { removeEventListener("message", on); reject(new Error("no answer")); }, timeoutMs);
    function on(e) {
      if (e.source !== window.parent || e.origin !== location.origin || !e.data || e.data.type !== INIT) return;
      const d = readDesignMessage(e.data);
      if (!d) return;
      clearTimeout(timer); removeEventListener("message", on); resolve(d);
    }
    addEventListener("message", on);
    window.parent.postMessage({ type: READY }, location.origin);
  });
}

// Gives the new design back to the page.
export function sendDesign(design) {
  window.parent.postMessage({ type: DONE, ...packDesign(design) }, location.origin);
}

// ---------- on the page ----------

// Opens the editor for a gift over the page.
//   shop, handle, title: the gift. design: { sides, side, sources, originals } to start from.
//   onDone({ sides, side, sources, originals }): the customer tapped "Use this". onClose(): the layer is gone (done or back).
// Returns { close }.
export function openAdjust({ shop, handle, title, design, onDone, onClose }) {
  const layer = document.createElement("div");
  layer.className = "adjust-layer"; layer.setAttribute("role", "dialog"); layer.setAttribute("aria-modal", "true"); layer.setAttribute("aria-label", "Adjust " + title);
  layer.innerHTML = `<header><button class="btn" type="button" data-back>Back</button><b>${esc(title)}</b></header>`;
  const frame = document.createElement("iframe");
  frame.className = "adjust-frame"; frame.title = "Adjust your design on " + title;
  const src = new URL("./", location.href);
  src.search = new URLSearchParams({ ...(shop ? { shop } : {}), handle, adjust: "1" }).toString();
  frame.src = src.href;
  layer.append(frame);
  document.body.append(layer);

  let closed = false;
  const close = () => {
    if (closed) return; closed = true;
    removeEventListener("message", on); clearTimeout(timer);
    layer.remove();
    if (onClose) onClose();
  };
  const timer = setTimeout(() => {          // the editor in the frame has not said it is ready
    const w = document.createElement("p"); w.className = "adjust-wait"; w.setAttribute("role", "status");
    w.textContent = "The editor is slow to open. Check your internet, or tap Back and try again.";
    layer.append(w);
  }, 15000);
  function on(e) {
    if (e.source !== frame.contentWindow || e.origin !== location.origin || !e.data) return;
    if (e.data.type === READY) {
      clearTimeout(timer);
      const w = layer.querySelector(".adjust-wait"); if (w) w.remove();
      frame.contentWindow.postMessage({ type: INIT, ...packDesign(design) }, location.origin);
    } else if (e.data.type === DONE) {
      const d = readDesignMessage(e.data);
      if (d) { close(); onDone(d); }
    }
  }
  addEventListener("message", on);
  layer.querySelector("[data-back]").addEventListener("click", close);
  layer.querySelector("[data-back]").focus();
  return { close };
}
